import * as Sentry from '@sentry/nextjs'

export enum TransactionNames {
  ADD_COMMENT_TO_NOTE = 'AddCommentToNote',
  CREATE_NOTE = 'CreateNote',
  GET_NOTES_BY_PATIENT = 'GetNotesByPatient',
  FETCH = 'Fetch',
  REGENERATE_ASSESSMENT_DOC = 'RegenerateAssessmentDoc',
  REGENERATE_ORDER_DOC = 'RegenerateOrderDoc',
  SUBMIT_ASSESSMENT = 'SubmitAssessment',
  MARK_NOTE_AS_CLOSED = 'MarkNoteAsClosed'
}

export enum SentrySources {
  ASSESSMENTS = 'assessments',
  CAREGIVER_ONBOARDING = 'caregiver-onboarding',
  CNA_VISITS_QA = 'cna-visits-qa',
  DOCUMENTS = 'documents',
  EMPLOYEE_BACKGROUND = 'employee-background',
  EMPLOYEE_LAYOUT = 'employee-layout',
  ENCOUNTER_SLICE = 'encounter-slice',
  FASTAPI_FETCH_MIDDLEWARE = 'fastapi-fetch-middleware',
  FEATURE_CONFIGS = 'feature-configs',
  FLOWS = 'flows',
  FORMAT_NOTE_DATA = 'format-note-data',
  HELPERS = 'helpers',
  INTERNAL_ONBOARDING = 'internal-onboarding',
  LOCATION_SLICE = 'location-slice',
  LOGIN = 'login',
  MAIN_WRAPPER = 'main-wrapper',
  MEDPLUM_FETCH_MIDDLEWARE = 'medplum-fetch-middleware',
  MEDPLUM_GRAPHQL_QUERY = 'medplum-graphql-query',
  MEDPLUM_INITIALIZATION = 'medplum-initialization',
  MEDPLUM_TOKEN_ISSUE = 'medplum-token-issue',
  MEDPLUM_USER_NOT_FOUND = 'medplum-user-not-found',
  NAV_BAR = 'nav-bar',
  NEXTJS_BUILD_ID = 'nextjs-build-id',
  ONBOARDING_PORTAL = 'onboarding-portal',
  ORDERS = 'orders',
  ORDERS_SLICE = 'orders-slice',
  PATIENT_ASSESSMENTS = 'patient-assessments',
  PATIENT_CARE_STATUS = 'patient-care-status',
  PATIENT_CHART = 'patient-chart',
  PATIENT_LAYOUT = 'patient-layout',
  PATIENT_NOTES = 'patient-notes',
  PATIENT_ONBOARDING = 'patient-onboarding',
  PATIENT_PROFILE = 'patient-profile',
  PATIENT_SLICE = 'patient-slice',
  PRACTITIONER_SLICE = 'practitioner-slice',
  PROVIDER_MODAL = 'provider-modal',
  RECERT_ASSESSMENT = 'recert-assessment',
  SCHEDULE_GENERATION = 'schedule-generation-warning',
  SCHEDULE_GENERATION_WARNING = 'schedule-generation-warning',
  SCHEDULING = 'scheduling',
  TABLE = 'table',
  TASKS = 'tasks',
  USE_PATIENT_NOTES = 'use-patient-notes',
  VISITS = 'visits',
  ADL_NEEDS = 'adl-needs',
  SEND_ORDER_DOCUMENT = 'send-order-document',
  ASSESSMENT_ADAPTER = 'assessment-adapter',
  PATIENT_FETCH = 'patient-fetch'
}

type Tags = {
  source: string
  operational?: string
  url?: string
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type Extras = {
  params?: any
  resources?: any
  query?: any
  info?: any
  errors?: Array<Record<string, any>>
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export enum SentryLevel {
  DEBUG = 'debug',
  LOG = 'log',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  FATAL = 'fatal'
}

export type SentryOptions = {
  transactionName?: TransactionNames
  tags?: Tags
  extras?: Extras
  level?: SentryLevel
}

const transformExtrasToJSON = (extras: Extras): Record<string, string> => {
  const transformed: Record<string, string> = {}

  for (const key in extras) {
    const value = extras[key]

    // Transform the value to a JSON string
    transformed[key] = JSON.stringify(value)
  }

  return transformed
}

export const captureException = (error: Error, SentryOptions?: SentryOptions): void => {
  const { transactionName, tags, extras, level } = SentryOptions || {}
  if (process.env.NODE_ENV !== 'development') {
    Sentry.withScope((scope) => {
      if (transactionName) {
        scope.setTransactionName(String(transactionName))
      }
      if (tags) {
        Object.keys(tags).forEach((key) => {
          scope.setTag(key, tags[key] || undefined)
        })
      }
      if (extras) {
        scope.setExtras(transformExtrasToJSON(extras))
      }
      if (level) {
        // level is overwritten if supplied, by default sentry captures error with level 'error'
        scope.setLevel(level)
      }
      Sentry.captureException(error)
    })
  } else {
    // eslint-disable-next-line no-console
    console.error('Sentry Error:', error, SentryOptions)
  }
}
